import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Spin,
  Empty,
  Button,
  Descriptions,
  Steps,
  Statistic,
  Space,
  message,
  Alert,
} from 'antd';
import {
  EnvironmentOutlined,
  CarOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  HomeOutlined,
  PhoneOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { orderService, deliveryLocationService } from '../services/api';
import './CustomerDashboard.css';

const { Title, Text, Paragraph } = Typography;

const statusSteps = [
  { title: 'Order Placed', key: 'pending' },
  { title: 'Confirmed', key: 'confirmed' },
  { title: 'Preparing', key: 'preparing' },
  { title: 'On the Way', key: 'on_the_way' },
  { title: 'Delivered', key: 'delivered' },
];

const statusStepMap = {
  pending: 0,
  confirmed: 1,
  accepted: 1,
  preparing: 2,
  packaging: 2,
  ready: 2,
  out_for_delivery: 3,
  on_the_way: 3,
  delivered: 4,
  completed: 4,
};

const DeliveryTracking = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) fetchData();
  }, [orderId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [orderRes, locRes] = await Promise.all([
        orderService.trackOrder(orderId).catch(() => null),
        deliveryLocationService.getDeliveryLocationByOrder(orderId).catch(() => null),
      ]);
      setOrder(orderRes?.data?.data || orderRes?.data);
      setDelivery(locRes?.data?.data || locRes?.data);
    } catch (err) {
      console.error('Failed to fetch tracking data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  if (!order)
    return (
      <Card>
        <Empty description="Order not found">
          <Link to="/orders">
            <Button>Back to Orders</Button>
          </Link>
        </Empty>
      </Card>
    );

  const currentStatus = order.order_status || order.status || 'pending';
  const currentStep = statusStepMap[currentStatus] ?? 0;
  const deliveryPerson = order.delivery_person || delivery?.delivery_man || delivery;

  return (
    <div className="customer-page" style={{ padding: 0 }}>
      <div
        className="customer-page-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Order #{orderId}
          </Title>
          <Tag
            color={
              currentStatus === 'delivered' || currentStatus === 'completed'
                ? 'green'
                : currentStatus === 'cancelled'
                  ? 'red'
                  : 'blue'
            }
            style={{ marginTop: 4 }}
          >
            {currentStatus.toUpperCase().replace(/_/g, ' ')}
          </Tag>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </div>

      {currentStatus === 'delivered' || currentStatus === 'completed' ? (
        <Alert
          message="Your order has been delivered!"
          type="success"
          showIcon
          style={{ margin: '16px 0' }}
        />
      ) : currentStatus === 'cancelled' ? (
        <Alert
          message="This order has been cancelled"
          type="error"
          showIcon
          style={{ margin: '16px 0' }}
        />
      ) : (
        <Alert
          message={delivery ? 'Your delivery is on the way!' : 'Your order is being processed'}
          type="info"
          showIcon
          style={{ margin: '16px 0' }}
        />
      )}

      <Card className="customer-card" style={{ marginBottom: 16 }}>
        <Steps
          current={currentStep}
          size="small"
          items={statusSteps.map((s, i) => ({
            title: s.title,
            status: i < currentStep ? 'finish' : i === currentStep ? 'process' : 'wait',
            icon:
              i < currentStep ? (
                <CheckCircleFilled />
              ) : i === currentStep ? (
                <CarOutlined />
              ) : undefined,
          }))}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            className="customer-card"
            title={
              <span>
                <EnvironmentOutlined /> Delivery Location
              </span>
            }
          >
            {deliveryPerson ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #52b788, #2d5a3d)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <UserOutlined style={{ fontSize: 36, color: '#fff' }} />
                </div>
                <Title level={5} style={{ margin: 0, color: '#2d5a3d' }}>
                  {deliveryPerson.full_name || deliveryPerson.name || 'Delivery Partner'}
                </Title>
                {deliveryPerson.phone_number && (
                  <Text type="secondary">
                    <PhoneOutlined /> {deliveryPerson.phone_number}
                  </Text>
                )}
                {delivery?.latitude && delivery?.longitude && (
                  <div
                    style={{ marginTop: 16, padding: 12, background: '#f0f5f0', borderRadius: 10 }}
                  >
                    <Text type="secondary">📍 Live Location</Text>
                    <div style={{ fontSize: 13, color: '#374151', wordBreak: 'break-all' }}>
                      Lat: {delivery.latitude}, Lng: {delivery.longitude}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Updated:{' '}
                      {delivery.updated_at || delivery.created_at
                        ? new Date(delivery.updated_at || delivery.created_at).toLocaleTimeString()
                        : 'N/A'}
                    </Text>
                  </div>
                )}
                {order.delivery_address && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      background: '#f0f5f0',
                      borderRadius: 10,
                      textAlign: 'left',
                    }}
                  >
                    <Text type="secondary">
                      <HomeOutlined /> Delivery to:
                    </Text>
                    <Text style={{ display: 'block', color: '#374151', fontSize: 13 }}>
                      {typeof order.delivery_address === 'object'
                        ? Object.values(order.delivery_address).filter(Boolean).join(', ')
                        : order.delivery_address}
                    </Text>
                  </div>
                )}
              </div>
            ) : (
              <Empty description="Delivery partner not yet assigned" />
            )}
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            className="customer-card"
            title={
              <span>
                <CarOutlined /> Order Summary
              </span>
            }
          >
            {order.items && Array.isArray(order.items) && (
              <div style={{ marginBottom: 16 }}>
                {order.items.slice(0, 5).map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '6px 0',
                      borderBottom:
                        i < Math.min(order.items.length, 5) - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <Text>
                      {item.product_service?.name || item.name || `Item #${item.id}`} x
                      {item.quantity || 1}
                    </Text>
                    <Text strong>
                      NPR{' '}
                      {((item.price_at_order || item.price || 0) * (item.quantity || 1)).toFixed(2)}
                    </Text>
                  </div>
                ))}
              </div>
            )}
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Total">
                <Text strong style={{ color: '#52b788', fontSize: 18 }}>
                  NPR {order.total_amount || 0}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Payment">
                {order.payment_method || order.payment_status || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Date">
                {order.created_at ? new Date(order.created_at).toLocaleString() : 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DeliveryTracking;
